import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Upload, CheckCircle2, AlertCircle, ArrowRight, Loader2, FileUp, Copy, Undo2 } from 'lucide-react';
import { requisitionsApi } from '../api';

const DATE_RE = /^\d{2}\/\d{2}\/\d{4}$/;

/** Split into rows on \n, cells on \t OR comma (auto-detect per line). */
function parseClipboardText(text) {
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  return lines.map(line => (line.includes('\t') ? line.split('\t') : line.split(','))
    .map(c => c.trim()));
}

const HEADERS = ['ebobranchcode', 'sku', 'qty', 'backdate'];
const isHeaderRow = (cells) => {
  const lower = cells.map(c => c.toLowerCase());
  return (lower.includes('ebobranchcode') || lower.includes('branchcode') || lower.includes('branch code')) &&
         (lower.includes('sku') || lower.includes('sku code'));
};

function rowsFromText(text) {
  const raw = parseClipboardText(text);
  if (raw.length === 0) return { headers: null, rows: [] };

  let headers = null;
  let dataStart = 0;
  if (isHeaderRow(raw[0])) {
    headers = raw[0].map(h => h.toLowerCase().replace(/\s+/g, ''));
    dataStart = 1;
  }
  const keyOrder = headers || HEADERS;

  return {
    headers,
    rows: raw.slice(dataStart).map(cells => {
      const row = {};
      keyOrder.forEach((k, i) => { if (cells[i] !== undefined) row[k] = cells[i]; });
      return {
        eboBranchCode: row.ebobranchcode || row.branchcode || row.branchCode || '',
        sku: (row.sku || '').toUpperCase(),
        qty: row.qty || '1',
        backdate: row.backdate || row.date || '',
      };
    }),
  };
}

function validate(row, validBranches) {
  if (!row.eboBranchCode) return { ok: false, error: 'eboBranchCode required' };
  if (validBranches.size > 0 && !validBranches.has(row.eboBranchCode)) {
    return { ok: false, error: `unknown EBO branch ${row.eboBranchCode}` };
  }
  if (!row.sku) return { ok: false, error: 'SKU required' };
  if (!DATE_RE.test(row.backdate)) return { ok: false, error: 'backdate must be DD/MM/YYYY' };
  const qty = Number(row.qty);
  if (Number.isNaN(qty) || qty < 1) return { ok: false, error: 'qty must be >= 1' };
  return { ok: true };
}

const EXAMPLE = `eboBranchCode\tsku\tqty\tbackdate
FMS-BLR01\tA001\t1\t15/06/2025
FMS-DEL02\tA042\t1\t22/07/2025
FMS-MUM01\tA015\t1\t03/09/2025`;

export default function BulkImportRequisitionsModal({ open, onClose, onImported, ebos = [], theme }) {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [fileError, setFileError] = useState('');
  const [result, setResult] = useState(null); // {inserted, duplicate, failed, batchId, rows}
  const [preflight, setPreflight] = useState(null); // {rows: [{status, reason, existingReqId?}]}
  const [undoing, setUndoing] = useState(false);
  const [undoResult, setUndoResult] = useState(null); // {deleted}
  const fileInputRef = useRef(null);

  const validBranches = useMemo(() => new Set((ebos || []).map(e => e.code)), [ebos]);
  const parsed = useMemo(() => rowsFromText(text), [text]);
  const validated = useMemo(() => parsed.rows.map(r => ({ ...r, ...validate(r, validBranches) })), [parsed, validBranches]);
  const validRows = validated.filter(r => r.ok);
  const invalidCount = validated.length - validRows.length;

  // Preflight — every time the client-validated rows change, ask the backend
  // to run the same insertion path in dry-run mode. This surfaces DUPLICATE
  // rows (same EBO + SKU + date already exists) BEFORE the user hits Import.
  useEffect(() => {
    if (!open || validRows.length === 0) { setPreflight(null); return; }
    const cleanRows = validRows.map(({ ok, error, ...rest }) => rest); // eslint-disable-line no-unused-vars
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await requisitionsApi.bulkHistoricalDryRun(cleanRows);
        if (!cancelled) setPreflight(r);
      } catch { /* ignore — user can still submit; server will re-validate */ }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [text, open, validRows.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const duplicateRows = preflight?.rows?.filter(r => r.status === 'duplicate') || [];
  const readyRows = preflight ? (preflight.ready || 0) : validRows.length;

  if (!open) return null;

  const submit = async () => {
    if (validRows.length === 0) return;
    setLoading(true);
    setResult(null);
    try {
      const cleanRows = validRows.map(({ ok, error, ...rest }) => rest); // eslint-disable-line no-unused-vars
      const r = await requisitionsApi.bulkHistorical(cleanRows);
      setResult(r);
      // Refresh callback fires but does NOT close the modal — the SA sees
      // the Created / Duplicates skipped / Failed tiles and dismisses via
      // the 'Done' button.
      if (r.inserted > 0) onImported?.(r);
    } catch (e) {
      setResult({ error: e.response?.data?.detail || 'Bulk import failed' });
    } finally {
      setLoading(false);
    }
  };

  const closeAndReset = () => {
    setText(''); setResult(null); setFileError(''); setPreflight(null); setUndoResult(null); onClose();
  };

  const undoImport = async () => {
    if (!result?.batchId || undoing) return;
    if (!window.confirm(`Undo this import? All ${result.inserted} requisition${result.inserted === 1 ? '' : 's'} inserted by this batch will be soft-deleted. Live workflow requisitions are never touched.`)) return;
    setUndoing(true);
    try {
      const r = await requisitionsApi.bulkHistoricalUndo(result.batchId);
      setUndoResult(r);
      onImported?.({ ...result, undo: r });
    } catch (e) {
      alert(e.response?.data?.detail || 'Undo failed');
    } finally { setUndoing(false); }
  };

  const onFilePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setFileError('');
    setParsing(true);
    try {
      const { rows } = await requisitionsApi.bulkHistoricalParseFile(file);
      if (!rows || rows.length === 0) {
        setFileError('The file has no parseable rows.');
        return;
      }
      // Convert parsed rows into the textarea format so the SA sees the exact
      // preview + can edit before importing.
      const tsv = ['eboBranchCode\tsku\tqty\tbackdate',
        ...rows.map(r => `${r.eboBranchCode}\t${r.sku}\t${r.qty || 1}\t${r.backdate}`)].join('\n');
      setText(tsv);
    } catch (err) {
      setFileError(err.response?.data?.detail || 'Failed to parse file');
    } finally {
      setParsing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={closeAndReset}>
      <div className="fm-card p-5 w-full max-w-[900px] max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()} data-testid="bulk-historical-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-semibold text-[16px]">Bulk import historical requisitions</div>
            <div className="text-[12px] text-[#7a6f5c] mt-0.5">
              Paste rows from Excel (tab-separated) or comma-separated. Each row becomes a fully-Delivered requisition backdated to the given date.
            </div>
          </div>
          <button onClick={closeAndReset} data-testid="bulk-historical-close-btn"><X size={16} /></button>
        </div>

        {!result && (
          <>
            <div className="mb-3 flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={onFilePick}
                data-testid="bulk-historical-file-input"
                className="hidden"
              />
              <button
                type="button"
                data-testid="bulk-historical-file-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={parsing}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-[13px] font-medium border border-[#E7E2D3] bg-white hover:bg-[#F7F4EA] disabled:opacity-50"
              >
                {parsing ? <Loader2 size={13} className="animate-spin" /> : <FileUp size={13} />}
                {parsing ? 'Parsing…' : 'Upload .xlsx or .csv'}
              </button>
              <span className="text-[11.5px] text-[#7a6f5c]">or paste rows manually below</span>
              {fileError && <span className="text-[11.5px]" style={{ color: '#B84A4A' }} data-testid="bulk-historical-file-error">{fileError}</span>}
            </div>

            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="text-[12.5px] font-medium block mb-1">Paste rows</label>
                <textarea
                  data-testid="bulk-historical-textarea"
                  value={text}
                  onChange={e => setText(e.target.value)}
                  placeholder={EXAMPLE}
                  spellCheck={false}
                  className="fm-focus w-full h-[240px] font-mono text-[12px] border border-[#E7E2D3] rounded-md p-3 bg-white leading-relaxed"
                />
              </div>
              <div className="text-[12.5px] text-[#7a6f5c]">
                <div className="font-semibold text-[#232323] mb-1">Column order</div>
                <ol className="list-decimal ml-4 space-y-0.5">
                  <li><span className="font-mono">eboBranchCode</span> — required, must exist in EBO master</li>
                  <li><span className="font-mono">sku</span> — required, must exist in Item master</li>
                  <li><span className="font-mono">qty</span> — optional, defaults to 1 (max 1)</li>
                  <li><span className="font-mono">backdate</span> — required, DD/MM/YYYY, must be ≤ today</li>
                </ol>
                <div className="mt-3 pt-3 border-t border-[#EFEADC]">
                  <div className="font-semibold text-[#232323] mb-1">Tip</div>
                  Rows land as fully-Delivered with all three dates (Requisition / Fulfilled / Delivered) stamped to the backdate. Cooldown is skipped and no warehouse email is fired.
                </div>
              </div>
            </div>

            {parsed.rows.length > 0 && (
              <div className="mb-3">
                <div className="flex items-center gap-3 mb-2 text-[12.5px] flex-wrap">
                  <span className="text-[#7a6f5c]">Parsed:</span>
                  <span className="font-semibold" data-testid="bulk-historical-total">{parsed.rows.length} rows</span>
                  <span className="inline-flex items-center gap-1 text-[#2f6b41]"><CheckCircle2 size={12} /> <span data-testid="bulk-historical-valid-count">{readyRows} ready</span></span>
                  {duplicateRows.length > 0 && (
                    <span className="inline-flex items-center gap-1 text-[#8B6A1F]" data-testid="bulk-historical-duplicate-count">
                      <Copy size={12} /> {duplicateRows.length} duplicate(s) will be skipped
                    </span>
                  )}
                  {invalidCount > 0 && <span className="inline-flex items-center gap-1" style={{ color: '#B84A4A' }}><AlertCircle size={12} /> {invalidCount} need fixing</span>}
                </div>
                <div className="border border-[#E7E2D3] rounded-md max-h-[240px] overflow-auto">
                  <table className="w-full text-[12.5px]">
                    <thead className="bg-[#F7F4EA] sticky top-0">
                      <tr className="text-left text-[11px] text-[#7a6f5c]">
                        <th className="px-2 py-1.5">#</th>
                        <th className="px-2 py-1.5">Branch</th>
                        <th className="px-2 py-1.5">SKU</th>
                        <th className="px-2 py-1.5">Qty</th>
                        <th className="px-2 py-1.5">Backdate</th>
                        <th className="px-2 py-1.5">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validated.map((r, i) => {
                        // Merge in the preflight status (duplicate / ready)
                        // if we have one for this triple.
                        const dupKey = `${r.eboBranchCode}|${r.sku}|${r.backdate}`;
                        const preflightRow = (preflight?.rows || []).find(pr =>
                          `${pr.eboBranchCode}|${pr.sku}|${pr.backdate}` === dupKey);
                        const isDup = preflightRow?.status === 'duplicate';
                        return (
                          <tr key={i} className="border-t border-[#EFEADC]" data-testid={`bulk-historical-row-${i}`}>
                            <td className="px-2 py-1.5 text-[#7a6f5c]">{i + 1}</td>
                            <td className="px-2 py-1.5 font-mono">{r.eboBranchCode || <span className="text-[#B84A4A]">—</span>}</td>
                            <td className="px-2 py-1.5 font-mono">{r.sku || <span className="text-[#B84A4A]">—</span>}</td>
                            <td className="px-2 py-1.5">{r.qty}</td>
                            <td className="px-2 py-1.5 font-mono">{r.backdate || <span className="text-[#B84A4A]">—</span>}</td>
                            <td className="px-2 py-1.5">
                              {!r.ok ? (
                                <span className="inline-flex items-center gap-1" style={{ color: '#B84A4A' }} title={r.error}><AlertCircle size={12} /> {r.error}</span>
                              ) : isDup ? (
                                <span className="inline-flex items-center gap-1 text-[#8B6A1F]" title={preflightRow?.reason} data-testid={`bulk-historical-row-${i}-duplicate`}>
                                  <Copy size={12} /> duplicate — will skip
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-[#2f6b41]"><CheckCircle2 size={12} /> ready</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <button onClick={closeAndReset} className="px-3 py-2 border border-[#E7E2D3] rounded-md text-[13.5px]">Cancel</button>
              <button
                onClick={submit}
                disabled={loading || validRows.length === 0}
                data-testid="bulk-historical-submit-btn"
                className="px-4 py-2 rounded-md text-white text-[13.5px] font-medium disabled:opacity-40 inline-flex items-center gap-2"
                style={{ background: theme.primary }}
              >
                {loading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                Import {readyRows} new row{readyRows === 1 ? '' : 's'}{duplicateRows.length > 0 ? ` · skip ${duplicateRows.length} duplicate${duplicateRows.length === 1 ? '' : 's'}` : ''}
              </button>
            </div>
          </>
        )}

        {result && (
          <div data-testid="bulk-historical-result">
            {result.error ? (
              <div className="p-4 rounded bg-[#F2E2E2] text-[#a04747] text-[13px]">{result.error}</div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <ResultTile label="Created" value={result.inserted} color="#2f6b41" />
                  <ResultTile label="Duplicates skipped" value={result.duplicate || 0} color="#8B6A1F" />
                  <ResultTile label="Failed" value={result.failed} color="#B84A4A" />
                </div>
                {(result.rows || []).some(r => r.status !== 'inserted') && (
                  <div className="border border-[#E7E2D3] rounded-md max-h-[220px] overflow-auto mb-4">
                    <table className="w-full text-[12px]">
                      <thead className="bg-[#F7F4EA] sticky top-0">
                        <tr className="text-left text-[11px] text-[#7a6f5c]">
                          <th className="px-2 py-1.5">#</th>
                          <th className="px-2 py-1.5">Branch</th>
                          <th className="px-2 py-1.5">SKU</th>
                          <th className="px-2 py-1.5">Result</th>
                          <th className="px-2 py-1.5">Detail</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.rows.map((r, i) => (
                          <tr key={i} className="border-t border-[#EFEADC]">
                            <td className="px-2 py-1.5 text-[#7a6f5c]">{(r.row ?? i) + 1}</td>
                            <td className="px-2 py-1.5 font-mono">{r.eboBranchCode}</td>
                            <td className="px-2 py-1.5 font-mono">{r.sku}</td>
                            <td className="px-2 py-1.5">
                              {r.status === 'inserted' && <span className="text-[#2f6b41]">✓ created</span>}
                              {r.status === 'duplicate' && <span className="text-[#8B6A1F]">⊘ skipped</span>}
                              {r.status === 'failed' && <span style={{ color: '#B84A4A' }}>✗ failed</span>}
                            </td>
                            <td className="px-2 py-1.5 text-[#7a6f5c]">{r.reason || r.reqId || ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="flex justify-end gap-2 items-center">
                  {result.batchId && result.inserted > 0 && !undoResult && (
                    <>
                      <div className="mr-auto text-[11.5px] text-[#7a6f5c] font-mono" data-testid="bulk-historical-batch-id">Batch {result.batchId}</div>
                      <button
                        onClick={undoImport}
                        disabled={undoing}
                        data-testid="bulk-historical-undo-btn"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-[13px] font-medium border border-[#E8D28C] bg-[#FBF3DA] hover:bg-[#F7E9CC] disabled:opacity-50"
                        style={{ color: '#8B6A1F' }}
                        title="Soft-delete only the requisitions from this batch — safe, reversible"
                      >
                        {undoing ? <Loader2 size={13} className="animate-spin" /> : <Undo2 size={13} />}
                        Undo this import
                      </button>
                    </>
                  )}
                  {undoResult && (
                    <div className="mr-auto text-[12.5px] text-[#8B6A1F]" data-testid="bulk-historical-undo-result">
                      ✓ Undone — {undoResult.deleted} requisition{undoResult.deleted === 1 ? '' : 's'} soft-deleted
                    </div>
                  )}
                  <button onClick={closeAndReset} data-testid="bulk-historical-done-btn" className="px-4 py-2 rounded-md text-white text-[13.5px] font-medium inline-flex items-center gap-2" style={{ background: theme.primary }}>
                    Done <ArrowRight size={13} />
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ResultTile({ label, value, color }) {
  return (
    <div className="border border-[#E7E2D3] rounded-md px-3 py-2 bg-white">
      <div className="text-[11px] text-[#7a6f5c]">{label}</div>
      <div className="text-[22px] font-semibold" style={{ color }}>{value}</div>
    </div>
  );
}
